import { useEffect, useRef, useState } from 'react';
import { toSpecialFixed } from '@/lib/misc/utils';
import { delay } from '../LitSessionContext/useLitAuth';
const WS_URL = 'wss://graph.codex.io/graphql';
const API_KEY = '44aec9191920ac623b5ec254ed50b13d68600a20';
export class TokenEventTracker {
  constructor(onMessageFn) {
    this.tokensToTrack = {};
    this.eventCount = 0;
    this.initHeartbeat();
    this.onMessageFn = onMessageFn;
  }
  initHeartbeat() {
    setInterval(() => {
      console.log(`Events heartbeat: ${this.eventCount} events, tracking ${Object.keys(this.tokensToTrack).length} tokens`);
      this.eventCount = 0;
    }, 1000 * 15);
  }
  getEventId(pairAddress, networkId) {
    return `events_${pairAddress}_${networkId}`;
  }
  getPairId(pairAddress, networkId) {
    return `${pairAddress}:${networkId}`;
  }
  createSubscriptionMessage(pairAddress, networkId) {
    const message = JSON.stringify({
      id: this.getEventId(pairAddress, networkId),
      type: 'subscribe',
      payload: {
        variables: {
          id: this.getPairId(pairAddress, networkId)
        },
        operationName: 'OnEventsCreated',
        query: `
                    subscription OnEventsCreated($id: String!) {
                        onEventsCreated(id: $id) {
                            events {
                                eventDisplayType
                                maker
                                token0SwapValueUsd
                                token1SwapValueUsd
                                timestamp
                                data {
                                  ... on SwapEventData {
                                    amountNonLiquidityToken
                                    priceBaseToken
                                    priceBaseTokenTotal
                                    priceUsd
                                    priceUsdTotal
                                    __typename
                                  }
                                }
                            }
                        }
                    }
                `
      }
    });
    return message;
  }
  async openEventSocket(pairAddress, networkId) {
    const pairId = this.getPairId(pairAddress, networkId);
    return new Promise((_, reject) => {
      const ws = new WebSocket(WS_URL, 'graphql-transport-ws'); // No need to specify protocol in browser WebSocket.
      ws.onopen = () => {
        console.log('tokennnn opened', pairAddress, networkId);
        ws.send(JSON.stringify({
          type: 'connection_init',
          payload: {
            Authorization: API_KEY
          }
        }));
      };
      ws.onmessage = event => {
        var _a, _b, _c;
        const data = JSON.parse(event.data);
        if (data.type === 'connection_ack') {
          ws.send(this.createSubscriptionMessage(pairAddress, networkId));
        } else if ((_c = (_b = (_a = data.payload) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.onEventsCreated) === null || _c === void 0 ? void 0 : _c.events) {
          this.eventCount++;
          const events = data.payload.data.onEventsCreated.events;
          this.onMessageFn(events);
        }
      };
      ws.onerror = error => {
        console.error(`WebSocket error for ${pairId}:`, error);
        reject(error);
      };
      ws.onclose = () => {
        console.log(`WebSocket closed for ${pairId}`);
        // Attempt to reconnect after 5 seconds
        setTimeout(async () => {
          await this.addTokenToTrack(pairId, networkId);
        }, 5000);
      };
    });
  }
  async addTokenToTrack(pairAddress, networkId) {
    const pairId = this.getPairId(pairAddress, networkId);
    try {
      if (this.tokensToTrack[pairId]) {
        return;
      }
      const ws = await this.openEventSocket(pairAddress, networkId);
      this.tokensToTrack[pairId] = {
        pairAddress,
        networkId,
        websocket: ws
      };
    } catch (error) {
      console.error(`Failed to track ${pairId}:`, error);
      throw error;
    }
  }
  async removeToken(pairAddress, networkId) {
    const pairId = this.getPairId(pairAddress, networkId);
    try {
      const tokenPair = this.tokensToTrack[pairId];
      if (tokenPair === null || tokenPair === void 0 ? void 0 : tokenPair.websocket) {
        tokenPair.websocket.send(JSON.stringify({
          type: 'complete',
          id: this.getEventId(pairAddress, networkId)
        }));
        tokenPair.websocket.close();
      }
      delete this.tokensToTrack[pairId];
      console.log(`Stopped tracking ${pairId}`);
    } catch (error) {
      console.error(`Failed to remove ${pairId}:`, error);
      throw error;
    }
  }
  async assertTokens(pairIdes) {
    const currentTokens = Object.keys(this.tokensToTrack);
    const tokensToRemove = currentTokens.filter(tokenPairId => !(pairIdes === null || pairIdes === void 0 ? void 0 : pairIdes.includes(tokenPairId)));
    await Promise.all(tokensToRemove.map(tokenPairId => this.removeToken(this.tokensToTrack[tokenPairId].pairAddress, this.tokensToTrack[tokenPairId].networkId)));
  }
}
export default function useTokenNotifications({
  address,
  network,
  enabled = true
}) {
  const [showOrder, setShowOrder] = useState(null);
  const [animationKey, setAnimationKey] = useState(0);
  const [latestEvents, setLatestEvents] = useState(null);
  const eventsUpdateCount = useRef(0);
  const lastSentRef = useRef(0);
  useEffect(() => {
    if (!enabled) return;
    const getTokenEvents = async () => {
      const data = await fetchTokenEvents(address, network);
      const events = data.items.map(event => {
        let amount = event.data.priceUsdTotal;
        if (parseFloat(amount) > 2000) {
          amount = `$${toSpecialFixed(parseFloat(amount) / 1000).value}K`;
        } else {
          amount = `$${toSpecialFixed(parseFloat(amount)).value}`;
        }
        return {
          type: event.eventDisplayType.toLowerCase(),
          amount
        };
      });
      eventsUpdateCount.current++;
      setLatestEvents({
        tradeEvents: events,
        updateCount: eventsUpdateCount.current
      });
    };
    const interval = setInterval(getTokenEvents, 8000); // Simulate every 3 seconds
    return () => clearInterval(interval); // Cleanup on unmount
  }, [address, network, enabled]);
  useEffect(() => {
    if (latestEvents === null || latestEvents === void 0 ? void 0 : latestEvents.tradeEvents) {
      latestEvents.tradeEvents.map(async (event, idx) => {
        if (latestEvents.updateCount !== eventsUpdateCount.current) {
          return;
        }
        await delay(400 * (Math.random() * 0.5 + 0.5) * idx);
        if (lastSentRef.current && Date.now() - lastSentRef.current < 500) {
          return;
        }
        setShowOrder(event);
        lastSentRef.current = Date.now();
        setAnimationKey(prevKey => prevKey + 1); // Trigger reanimation
      });
    }
  }, [latestEvents, enabled]);
  return {
    showOrder,
    animationKey
  };
}
async function fetchTokenEvents(address, networkId, cursor, limit = 50, maxRetries = 1) {
  const query = `
    query GetTokenEvents($cursor: String, $direction: RankingDirection, $limit: Int, $query: EventsQueryInput!) {
      getTokenEvents(cursor: $cursor, direction: $direction, limit: $limit, query: $query) {
        cursor
        items {
          address
          baseTokenPrice
          blockHash
          blockNumber
          eventDisplayType
          eventType
          id
          logIndex
          maker
          networkId
          quoteToken
          timestamp
          token0PoolValueUsd
          token0SwapValueUsd
          token0ValueBase
          token1PoolValueUsd
          token1SwapValueUsd
          token1ValueBase
          transactionHash
          transactionIndex
          data {
            ... on SwapEventData {
              amount0In
              amount0Out
              amount1In
              amount1Out
              amountNonLiquidityToken
              priceBaseToken
              priceBaseTokenTotal
              priceUsd
              priceUsdTotal
              __typename
            }
          }
        }
      }
    }
  `;
  const variables = {
    cursor,
    direction: 'ASC',
    limit,
    query: {
      address,
      networkId,
      eventDisplayType: ['Buy', 'Sell'],
      eventType: 'Swap',
      priceUsdTotal: {
        // Add minimum USD value filter
        gte: 20
      }
    }
  };
  let lastError = null;
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const response = await fetch('https://graph.codex.io/graphql', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: '44aec9191920ac623b5ec254ed50b13d68600a20'
        },
        body: JSON.stringify({
          query,
          variables
        })
      });
      // Check if it's a timeout error
      if (response.status === 504) {
        const retryDelay = Math.min(1000 * Math.pow(2, attempt - 1), 8000); // Exponential backoff, max 8 seconds
        console.log(`Gateway timeout encountered. Attempt ${attempt}/${maxRetries}. Retrying in ${retryDelay}ms...`);
        await new Promise(resolve => setTimeout(resolve, retryDelay));
        continue;
      }
      const data = await response.json();
      if (data.errors) {
        throw new Error(data.errors[0].message);
      }
      // Delay
      await new Promise(resolve => setTimeout(resolve, 500));
      return data.data.getTokenEvents;
    } catch (error) {
      lastError = error;
      // If it's the last attempt, throw the error
      // if (attempt === maxRetries) {
      //   throw new Error(`Failed after ${maxRetries} attempts. Last error: ${lastError.message}`);
      // }
      // For other errors, retry with exponential backoff
      // const retryDelay = Math.min(1000 * Math.pow(2, attempt - 1), 8000);
      // await new Promise(resolve => setTimeout(resolve, retryDelay));
      return {
        cursor: '',
        items: []
      };
    }
  }
  // This should never be reached due to the throw in the last iteration
  throw lastError || new Error('Unknown error occurred');
}
// interface EventsQueryInput {
//   address: string;
//   amountNonLiquidityToken?: NumberFilter;
//   eventDisplayType?: EventDisplayType[];
//   eventType?: EventType;
//   maker?: string;
//   networkId: number;
//   priceBaseToken?: NumberFilter;
//   priceBaseTokenTotal?: NumberFilter;
//   priceUsd?: NumberFilter;
//   priceUsdTotal?: NumberFilter;
//   quoteToken?: QuoteToken;
//   timestamp?: EventQueryTimestampInput;
// }
// interface NumberFilter {
//   gt?: number;
//   gte?: number;
//   lt?: number;
//   lte?: number;
// }
// interface EventQueryTimestampInput {
//   gt?: number;
//   gte?: number;
//   lt?: number;
//   lte?: number;
// }